/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 31 Milk St # 960789 Boston, MA 02196 USA.
 *
 * Copyright (c) 2022-2026 (original work) Open Assessment Technologies SA;
 */


import { get, writable } from 'svelte/store';
import { getDefaultTranslations, DEFAULT_LOCALE } from '../helpers/testHelper';
import { fetchConfiguration } from '../services/configuration.service.js';
import apiConfig from '../core/apiConfig.js';
import _ from 'lodash';

export const DEFAULT_CONFIGS = {
    showBandwidthMessage: true,
    browserCompatibility: {
        enabled: true,
        accepted: {
            chrome: '>=102',
            edge: '>=102',
            firefox: '>=101',
            safari: '>=15.4',
            'mobile safari': '>=15.4',
            'Tao Secure Browser': '>=1.7.2'
        }
    },
    connectionCompatibility: {
        enabled: true,
        download: {
            serverUrl: apiConfig.API_URL,
            acceptable: 4194304,
            measures: [
                {
                    size: 1000,
                    timeout: 10000,
                    thread: 4
                }
            ]
        },
        upload: {
            serverUrl: apiConfig.API_URL,
            acceptable: 1258291,
            measures: [
                {
                    size: 100,
                    timeout: 10000,
                    thread: 4
                }
            ]
        },
        latency: {
            serverUrl: apiConfig.API_URL,
            acceptable: 800,
            measures: 5
        },
        jitter: {
            serverUrl: apiConfig.API_URL,
            measures: 5
        },
        concurrentUsers: 50
    },
    audioCompatibility: {
        enabled: true,
        download: {
            serverUrl: apiConfig.API_URL,
            acceptable: 4194304,
            measures: [
                {
                    size: 1000,
                    timeout: 10000,
                    thread: 4
                }
            ]
        },
        upload: {
            serverUrl: apiConfig.API_URL,
            acceptable: 1258291,
            measures: [
                {
                    size: 100,
                    timeout: 10000,
                    thread: 4
                }
            ]
        },
        latency: {
            serverUrl: apiConfig.API_URL,
            acceptable: 800,
            measures: 5
        },
        jitter: {
            serverUrl: apiConfig.API_URL,
            measures: 5
        },
        concurrentUsers: 50
    },
    videoCompatibility: {
        enabled: true,
        download: {
            serverUrl: apiConfig.API_URL,
            acceptable: 4194304,
            measures: [
                {
                    size: 1000,
                    timeout: 10000,
                    thread: 4
                }
            ]
        },
        upload: {
            serverUrl: apiConfig.API_URL,
            acceptable: 1258291,
            measures: [
                {
                    size: 100,
                    timeout: 10000,
                    thread: 4
                }
            ]
        },
        latency: {
            serverUrl: apiConfig.API_URL,
            acceptable: 800,
            measures: 5
        },
        jitter: {
            serverUrl: apiConfig.API_URL,
            measures: 5
        },
        concurrentUsers: 50
    },
    webSocketConnection: {
        enabled: true,
        url: apiConfig.WEBSOCKET_URL,
        path: apiConfig.WEBSOCKET_PATH,
        timeout: 40000,
    },
    endpointsReachability: {
        enabled: true,
        serviceEndpoints: {},
        cdnEndpoints: {},
        timeout: 10000
    },
    audio: {
        enabled: true,
        required: false
    },
    video: {
        enabled: false,
        required: false
    },
    fields: {
        additionalData: [],
    },
    secureBrowserCompatibility: {
        enabled: true,
        downloads: {},
        minimumVersion: '',
        redirectUrl: '',
        portalDownloadPage: '',
        providerId: '',
        installationInstructions: [],
        processDenyList: []
    }
};

function createConfigurationStore() {
    const configuration = writable(DEFAULT_CONFIGS);
    const { set, update, subscribe } = configuration;
    let resolveLoading = () => {};
    const loadingPromise = new Promise((resolve) => {
        resolveLoading = resolve;
    });

    return {
        loadingPromise,
        get() {
            return get(configuration);
        },
        set,
        update,
        subscribe,

        /**
         * @param {{}} partialConfig
         */
        merge(partialConfig) {
            this.update(config => (_.merge(
                config,
                partialConfig,
            )));
        },

        async load(locale) {
            let response;

            try {
                let configResponse = await fetchConfiguration(locale);
                response = await configResponse.json();

                if (!response[locale] || _.isEmpty(response[locale])) {
                    configResponse = await fetchConfiguration(DEFAULT_LOCALE);
                    response = await configResponse.json();
                }
            } catch (err) {
                /* eslint-disable-next-line */
                console.error(`Error fetching ${locale} locale config: ${err}`);
                if (process.env.NODE_ENV === 'development') {
                    /* eslint-disable-next-line */
                    console.info(`Serving fallback translations locally`);
                    response = getDefaultTranslations();
                }
            } finally {
                resolveLoading();
                this.merge(response);
            }
        }
    };
}

export default createConfigurationStore();
